import memoize from 'lodash/memoize';
import {
	type Action,
	createActionsHook,
	createContainer,
	createHook,
	createSelector,
	createStateHook,
	createStore,
} from 'react-sweet-state';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import uuid from 'uuid/v4';

import { fg } from '@atlaskit/platform-feature-flags';
import { type EntityATI, type ThirdPartyFilters } from '@atlassian/search-client';
import {
	type ExperimentConfig,
	getBackendExperiment,
	getMutuallyExclusiveUIExperiment,
	STATSIG_FULL_PAGE_SEARCH_LAYER,
	STATSIG_QUICK_FIND_LAYER,
} from '@atlassian/search-experiment';

import { ApplicationModes } from '../../../common/constants/application-modes';
import type { BootstrapState } from '../../../common/constants/bootstrap';
import { SEARCH_PAGE } from '../../../common/constants/experience';
import { TypeFilterValueKey } from '../../../common/constants/filters/universal-type';
import { ProductKeys } from '../../../common/constants/products';
import { type ThirdPartyConfigsBootstrap } from '../../../common/constants/schemas/3p-config';
import {
	productKeySchema,
	type SearchQueryStringParams,
	type SearchQueryStringParamsRaw,
} from '../../../common/constants/schemas/query-params';
import {
	type UserDetailsWithPicture,
	type UserDetailsWithStatus,
} from '../../../common/constants/types';
import { getApplicationMode, getUser } from '../../../common/utils/bootstrap';
import { isHello } from '../../../common/utils/is-hello';
import {
	areAllEqualQueryParams,
	parseQueryParams,
	toQueryParamsRaw,
	toQueryString,
} from '../../../common/utils/query-params';

/**
 * This state object contains all the configuration which must be loaded to make the page functional.
 */
export const initialState = {
	products: [],
	nouns: [],
	allFilterExcludedProducts: [],
	thirdPartyConfigs: {} as ThirdPartyConfigsBootstrap,
	searchSessionId: '',
	queryParams: {},
	prevQueryParams: {},
	initialSpaceARIs: [],
	experimentConfig: {
		backendExperiment: undefined,
		frontendExperiments: undefined,
		dynamicExperiments: undefined,
		isMutuallyExclusive: false,
		combinedExperiment: undefined,
	},
	experimentLayers: [],
	isNounsAvailable: false,
	isRovoEnabled: false,
	isConfigLoaded: false,
	rovoFeatures: {},
	isJiraIssueIndexed: false,
	productEdition: undefined,
	productDataRegions: undefined,
	linkedCloudARIs: undefined,
	analyticsAttributes: undefined,
	aggAbsoluteUrl: undefined,
	cloudId: undefined,
	aggregatorHostName: undefined,
	SAINHostName: undefined,
	user: undefined,
	primaryProduct: undefined,
	orgId: undefined,
	intercomHmac: undefined,
	referrer: undefined,
	initialTextQueryParam: undefined,
	browserHistoryItems: undefined,
	onHistoryHeadingClick: undefined,
	features: undefined,
	hasEditOrViewPlanPermission: false,
} satisfies BootstrapState;

export const actions = {
	setState:
		(partialState: Partial<BootstrapState>): Action<BootstrapState> =>
		({ setState }) => {
			setState(partialState);
		},
	setQueryParams:
		(
			queryParam: Partial<SearchQueryStringParams>,
		): Action<BootstrapState, BootstrapContainerProps> =>
		({}, { queryParams, queryParamsCallback = () => {} }) => {
			const areEqualQueryParams = areAllEqualQueryParams(
				queryParam,
				parseQueryParams(queryParams ?? {}),
			);

			if (areEqualQueryParams) {
				return;
			}

			queryParamsCallback(toQueryParamsRaw(queryParam));
		},
	syncQueryParams:
		(): Action<BootstrapState, BootstrapContainerProps> =>
		({ setState, getState }, { queryParams, queryParamsCallback = () => {} }) => {
			// queryParams is the query params coming from the URL or React Router in this case
			const {
				prevQueryParams, // Previously saved query params - this is to prevent infinitely updating queryParams
				queryParams: diffQueryParamsState, // The 'internal' query params state
			} = getState();

			const queryParamsURL = parseQueryParams(queryParams ?? {});

			const prevQueryParamsString = toQueryString(prevQueryParams);
			const diffQueryParamsURLString = toQueryString(queryParamsURL);
			const diffQueryParamsStateString = toQueryString(diffQueryParamsState);

			const allQueryParamsAreEqual = areAllEqualQueryParams(
				queryParamsURL,
				prevQueryParams,
				diffQueryParamsState,
			);

			if (allQueryParamsAreEqual) {
				return;
			}

			// Any changes from the url takes the highest precedence
			const hasURLQueryParams = !!queryParams;
			if (hasURLQueryParams && prevQueryParamsString !== diffQueryParamsURLString) {
				setState({ prevQueryParams: queryParamsURL, queryParams: queryParamsURL });
				return;
			}

			if (prevQueryParamsString !== diffQueryParamsStateString) {
				//  NOTE: If you are running the callback and you don't see the query params being updated,
				//  the callback might be sanitising the query parameters. This is the case for Confluence.
				queryParamsCallback(toQueryParamsRaw(diffQueryParamsState));
				setState({ prevQueryParams: diffQueryParamsState, queryParams: diffQueryParamsState });
				return;
			}
		},
	refreshSearchSessionId:
		(): Action<BootstrapState, BootstrapContainerProps> =>
		({ setState }) => {
			// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
			setState({ searchSessionId: uuid() });
		},
	clearReferrer:
		(): Action<BootstrapState, BootstrapContainerProps> =>
		({ setState }) => {
			setState({ referrer: undefined });
		},
};

const buildProducts = memoize(
	(a: ProductKeys[], b: ProductKeys[]) => {
		return [...new Set([...a, ...b])];
	},
	(a: ProductKeys[], b: ProductKeys[]) => a.join('') + b.join(''),
);

type TActions = typeof actions;

// Keep store private and use hooks to access state and actions
const Store = createStore<BootstrapState, TActions>({ initialState, actions, name: 'bootstrap' });

export const bootstrapStoreContainerActions = {
	onInit:
		(): Action<BootstrapState, BootstrapContainerProps> =>
		({ getState, setState }, { initialProps, queryParams }) => {
			const parsedQueryParams = queryParams && parseQueryParams(queryParams);
			setState({
				...getState(),
				...initialProps,
				...(queryParams && { queryParams: parsedQueryParams }),
				referrer: parsedQueryParams?.referrer,
				initialTextQueryParam: parsedQueryParams?.text,
				// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
				searchSessionId: initialProps.searchSessionId || uuid(),
			});
		},
	onUpdate:
		(): Action<BootstrapState, BootstrapContainerProps> =>
		({ setState, getState, dispatch }, { initialProps, queryParams }) => {
			const parsedQueryParams = queryParams && parseQueryParams(queryParams);

			const products = buildProducts(getState().products, initialProps?.products ?? []);

			setState({ ...getState(), ...initialProps, products, referrer: parsedQueryParams?.referrer });

			dispatch(actions.syncQueryParams());
		},
};

export type BootstrapContainerProps = {
	initialProps: Partial<BootstrapState>;
	queryParams?: Record<string, any>;
	/** Called when FPS query params should be updated in the application's router. */
	queryParamsCallback?: (queryParams: Partial<SearchQueryStringParamsRaw>) => void;
	/**
	 * @private
	 * @deprecated this prop is deprecated and not used anymore. It will be removed soon.
	 *
	 * @returns the space IDs for the given space keys. NOTE: these should be the space IDs, not ARIs.
	 * @example getSpaceIdsFromSpaceKeys(["STATUS"]); // Promise<["123454321"]>
	 **/
	getSpaceIdsFromSpaceKeys?: (spaceKeys: string[]) => Promise<string[]>;
};

export const BootstrapStoreContainer = createContainer<
	BootstrapState,
	TActions,
	BootstrapContainerProps
>(Store, bootstrapStoreContainerActions);

// Hooks

// Note - This doesn't trigger a re-render
export const useBootstrapActions = createActionsHook(Store);

export const useBootstrap = createHook(Store);

/**
 * @deprecated
 * See useBootstrapState in bootstrap/accessors/index.tsx
 */
export const useBootstrapState = createStateHook(Store, {
	selector: (state: BootstrapState, cb: (state: BootstrapState) => unknown) => cb(state),
}) as unknown as <T>(cb: (state: BootstrapState) => T) => T;

/**
 * This hook gets the extra analytics attributes that are passed in as a prop from the consumer.
 * It should not be confused with the hooks for firing analytics events in `controllers/use-analytics`
 */
export const useAnalyticsAttributes = createHook(Store, {
	selector: (state) => state.analyticsAttributes,
});

export const useAvailableProducts = createStateHook(Store, { selector: (state) => state.products });

export const useAvailableNouns = createStateHook(Store, { selector: (state) => state.nouns });

export const useIsAvailableProductsLoaded = createStateHook(Store, {
	selector: (state) => state.products && state.products.length > 0,
});

export const useBootstrapLoading = createHook(Store, {
	selector: (state) => state.products.length === 0,
});

export const usePrimaryProduct = createHook(Store, { selector: (state) => state.primaryProduct });

export const useSearchSessionId = createHook(Store, { selector: (state) => state.searchSessionId });

export const useIsHello = createHook(Store, { selector: (state) => isHello(state.cloudId) });

export const useFeatures = createHook(Store, { selector: (state) => state.features });

export const useBackendExperiment = createHook(Store, {
	selector: (state, args: { entities: EntityATI[]; thirdPartyFilters: ThirdPartyFilters }) => {
		return getBackendExperiment({
			experimentConfig: state.experimentConfig,
			entities: args.entities,
			productEdition: state.productEdition,
			isHello: isHello(state.cloudId),
			productDataRegions: state.productDataRegions,
			experience: SEARCH_PAGE,
			tenantId: state.cloudId,
			isJiraIssueIndexed: state.isJiraIssueIndexed,
			thirdPartyFilters: args.thirdPartyFilters,
		});
	},
});

export const useFrontendExperiments = createHook(Store, {
	selector: (state) => state.experimentConfig.frontendExperiments,
});

export const getMultiSiteSearchInRovo = (experimentConfig: ExperimentConfig) => {
	const cohort = experimentConfig.frontendExperiments?.['multi_site_search_in_rovo']?.cohort;

	return cohort === 'test';
};

export const useMultiSiteSearchInRovo = createStateHook(Store, {
	selector: (state) => getMultiSiteSearchInRovo(state.experimentConfig),
});

export const useCurrentUserId = createStateHook(Store, {
	selector: (state) => state.user?.id,
});

/**
 * >>> Confluence QuickFind UserShard Search Experiments
 *
 * We have 2 separated experiments: one is for hello tenant, the other is for 24 non-hello tenants.
 *
 * Experiment: `quick-find-user-shard-search-experiment`
 * + hello only
 * + statsig_url = https://go/statsig/experiments/quick-find-user-shard-search-experiment
 *
 * Experiment: `confluence-quickfind-usershard-nonhello-exp`
 * + 24 non-hello tenants
 * + statsig_url = https://go/statsig/experiments/confluence-quickfind-usershard-nonhello-exp
 *
 * These experiments have 3 groups:
 * + cohort = 'control', the current product.
 * + cohort = 'model-only', the model is served without any changes to the UI.
 * + cohort = 'test', both the model and new UI are served.
 *
 */

const USER_SHARD_EXPERIMENTS = [
	'quick-find-user-shard-search-experiment',
	'confluence-quickfind-usershard-nonhello-exp',
] as const;

type UserShardExperimentDetails = {
	cohort?: string;
	spaceL1Id?: string;
	pageL1Id?: string;
};

const getUserShardExperimentDetails = (
	experimentConfig: ExperimentConfig,
): UserShardExperimentDetails => {
	if (fg('refactor_mutually_exclusive_search_ui_layers')) {
		for (const experimentName of USER_SHARD_EXPERIMENTS) {
			const experiment = getMutuallyExclusiveUIExperiment(
				experimentName,
				STATSIG_QUICK_FIND_LAYER,
				{ fireLayerExposure: false },
			);
			if (experiment !== undefined) {
				return {
					cohort: experiment.get<string | undefined>('cohort', undefined),
					spaceL1Id: experiment.get<string | undefined>('spaceL1Id', undefined),
					pageL1Id: experiment.get<string | undefined>('pageL1Id', undefined),
				};
			}
		}

		return {};
	}

	let cohort: string | undefined;
	let spaceL1Id: string | undefined;
	let pageL1Id: string | undefined;

	USER_SHARD_EXPERIMENTS.forEach((experimentName) => {
		const experiment = experimentConfig.frontendExperiments?.[experimentName] as unknown as
			| {
					cohort?: string;
					spaceL1Id?: string;
					pageL1Id?: string;
			  }
			| undefined;

		cohort = cohort ?? experiment?.cohort;
		if (spaceL1Id === undefined) {
			spaceL1Id = experiment?.spaceL1Id;
		}
		if (pageL1Id === undefined) {
			pageL1Id = experiment?.pageL1Id;
		}
	});

	return { cohort, spaceL1Id, pageL1Id };
};

/**
 * Returns all user shard experiment flags in a single pass.
 * More efficient when multiple flags are needed simultaneously.
 */
export const getUserShardExperimentFlags = (experimentConfig: ExperimentConfig) => {
	const { cohort } = getUserShardExperimentDetails(experimentConfig);

	return {
		serveModel: cohort === 'model-only' || cohort === 'test',
		serveUI: cohort === 'test',
		serveControl: cohort === 'control',
		cohort, // Expose for debugging/logging
	};
};

export const serveUserShardModel = (experimentConfig: ExperimentConfig): boolean => {
	return getUserShardExperimentFlags(experimentConfig).serveModel;
};

export const serveUserShardUI = (experimentConfig: ExperimentConfig): boolean => {
	return getUserShardExperimentFlags(experimentConfig).serveUI;
};

export const serveUserShardControl = (experimentConfig: ExperimentConfig): boolean => {
	return getUserShardExperimentFlags(experimentConfig).serveControl;
};

export const getUserShardSpaceL1Id = (experimentConfig: ExperimentConfig): string | undefined => {
	return getUserShardExperimentDetails(experimentConfig).spaceL1Id;
};

export const getUserShardPageL1Id = (experimentConfig: ExperimentConfig): string | undefined => {
	return getUserShardExperimentDetails(experimentConfig).pageL1Id;
};

export const useUserShardModel = createStateHook(Store, {
	selector: (state) => serveUserShardModel(state.experimentConfig),
});

export const useUserShardUI = createStateHook(Store, {
	selector: (state) => serveUserShardUI(state.experimentConfig),
});

/* <<< Confluence QuickFind UserShard Search Experiments */

export const useExperimentConfig = createHook(Store, {
	selector: (state) => state.experimentConfig,
});

export const useIntercomHmac = createHook(Store, { selector: (state) => state.intercomHmac });

export const useIsNounsAvailable = createHook(Store, {
	selector: (state) => state.isNounsAvailable,
});

// We're intentionally not exposing a simple boolean (eg. isRovoEnabled) as that will not scale if more modes are introduced.
export const useApplicationMode = createStateHook(Store, {
	selector: createSelector(
		[(state) => state.isRovoEnabled],
		(isRovoEnabled): ApplicationModes => getApplicationMode({ isRovoEnabled }),
	),
});

export const useUser = createHook(Store, {
	selector: (state): UserDetailsWithStatus => {
		const user = state.user satisfies UserDetailsWithPicture | undefined;
		return getUser(user);
	},
});

export const useReferrer = createHook(Store, { selector: (state) => state.referrer });

export const useInitialTextQueryParam = createStateHook(Store, {
	selector: (state) => state.initialTextQueryParam,
});

export const useOrgId = createHook(Store, { selector: (state) => state.orgId });

export const useCloudId = createHook(Store, {
	selector: createSelector([(state) => state.cloudId], (cloudId) => cloudId),
});

export const useLinkedCloudARIs = createHook(Store, { selector: (state) => state.linkedCloudARIs });

export const useAutoSelectPrimaryProduct = createStateHook(Store, {
	selector: createSelector(
		[
			(state) => state.products,
			(state) => state.primaryProduct,
			(state) => state.isRovoEnabled,
			(state) => state.queryParams.product,
			(state) => state.queryParams.cql,
		],
		(products, primaryProduct, isRovoEnabled, currentSelectedProduct, currentCql) => {
			const applicationMode = getApplicationMode({ isRovoEnabled });
			const primaryProductToSelect = productKeySchema.safeParse(primaryProduct);

			const shouldAutoSelectPrimaryProduct =
				!currentSelectedProduct &&
				!currentCql &&
				products &&
				products.length > 0 &&
				applicationMode === ApplicationModes.Unified &&
				primaryProductToSelect.success &&
				primaryProductToSelect.data !== ProductKeys.Atlas;

			if (shouldAutoSelectPrimaryProduct) {
				return primaryProductToSelect.data;
			} else {
				return null;
			}
		},
	),
});

export const useIsConfigLoaded = createStateHook(Store, {
	selector: createSelector([(state) => state.isConfigLoaded], (isConfigLoaded) => isConfigLoaded),
});

/**
 * Note: this hook is only temporary and should be removed once Rovo is enabled for all users
 */
export const useIsRovoFree = createHook(Store, { selector: (state) => !!state.rovoFeatures?.scas });

export const useProductEdition = createHook(Store, { selector: (state) => state.productEdition });

export const useIsTypeFilteredToIncident = createStateHook(Store, {
	selector: (state) => state.queryParams?.type === TypeFilterValueKey.Incident,
});

export const useThirdPartyConfigs = createHook(Store, {
	selector: (state) => state.thirdPartyConfigs,
});

export const useUserRelevantDocumentsExperimentEnabled = createHook(Store, {
	selector: (state) => {
		if (fg('refactor_mutually_exclusive_search_ui_layers')) {
			const experiment = getMutuallyExclusiveUIExperiment(
				'quick_find_user_relevant_documents_experiment',
				STATSIG_QUICK_FIND_LAYER,
				{ fireLayerExposure: false },
			);
			const cohort = experiment?.get<string | undefined>('cohort', undefined);
			return cohort === 'test';
		}

		return !!state.experimentConfig.frontendExperiments?.[
			'quick_find_user_relevant_documents_experiment'
		]?.serveExperiment;
	},
});

export const useBrowserHistoryItems = createHook(Store, {
	selector: (state) => state.browserHistoryItems,
});

export const useFiltersDiscoverability2Enabled = createStateHook(Store, {
	selector: (state) => {
		const primaryProduct = state.primaryProduct;
		if (!primaryProduct) {
			return false;
		}

		return primaryProduct === ProductKeys.Confluence || primaryProduct === ProductKeys.Jira;
	},
});

export const serve3pGroupingExperiment = (fireLayerExposure: boolean = false) => {
	const experiment = getMutuallyExclusiveUIExperiment(
		'fps_3p_grouping_experiment',
		STATSIG_FULL_PAGE_SEARCH_LAYER,
		{ fireLayerExposure },
	);
	if (experiment !== undefined) {
		return experiment.get<string | undefined>('cohort', undefined);
	}
	return undefined;
};
